import { api } from '@/shared/api/client';
import { toParticipantPage, type MapperConfig } from '@/shared/lib/pax.mapper';
import type { ParticipantPage, RawPaxPage } from '@/shared/types/pax';

export interface ListParticipantsParams {
  miceEventId: number;
  /** Chave do status — uma das que a parametrização declara. */
  group: string;
  /** Nome do perfil (trilha). Vazio lista o grupo inteiro. */
  profile: string;
  /** Página, base 1 (a API chama de `offset`). */
  page: number;
  pageSize: number;
  /** De-para necessário para normalizar a resposta (grupo e e-mail). */
  mapper: MapperConfig;
  signal?: AbortSignal;
}

/**
 * Lista participantes de um grupo. A API já devolve paginado; aqui a resposta é
 * normalizada antes de chegar à UI (ver `pax.mapper`).
 *
 * Rota do módulo parametrizado (`/approval/paxes`), e não a legada (`/paxes`): lá
 * o evento, os IDs de grupo e os atributos das colunas estavam fixos no código da
 * API. Aqui vai o `miceEventId` e a API carrega o resto da parametrização gravada
 * no banco.
 *
 * A trilha vira `perfil` — o valor do atributo de perfil no MICE — em vez do
 * `enabled` de antes, que ligava/desligava a checagem de um atributo marcador
 * fixo na API.
 */
export async function listParticipants({
  miceEventId,
  group,
  profile,
  page,
  pageSize,
  mapper,
  signal,
}: ListParticipantsParams): Promise<ParticipantPage> {
  const { data } = await api.get<RawPaxPage>('/approval/paxes', {
    params: {
      miceEventId,
      grupo: group,
      offset: page,
      limit: pageSize,
      ...(profile ? { perfil: profile } : {}),
    },
    ...(signal ? { signal } : {}),
  });

  return toParticipantPage(data, mapper);
}

export interface MoveParticipantParams {
  miceEventId: number;
  paxId: number;
  /** Chave do status de destino — uma das que a parametrização declara. */
  group: string;
}

export interface MoveParticipantResult {
  /**
   * Se o participante foi avisado por e-mail. A decisão é gravada de todo modo: o
   * envio acontece depois e não a desfaz. A rota legada respondia vazio, e não
   * havia como saber.
   */
  emailSent: boolean;
}

/**
 * Move um participante de grupo. O back-end grava o grupo e o status de confirmação
 * lendo os dois da parametrização do evento, e dispara o e-mail do novo status.
 *
 * Rota do módulo parametrizado (`PUT /approval/paxes`), e não a legada (`PUT /paxes`),
 * que resolvia evento, grupo e `statusConfirmacao` por de-paras fixos no código —
 * em arquivos diferentes, que podiam divergir.
 */
export async function moveParticipant({ miceEventId, paxId, group }: MoveParticipantParams): Promise<MoveParticipantResult> {
  const { data } = await api.put<MoveParticipantResult>('/approval/paxes', { miceEventId, paxId, grupo: group });

  // A API pode responder sem corpo em um deploy antigo: sem informação, o mais
  // seguro é não afirmar que o aviso saiu.
  return { emailSent: data?.emailSent === true };
}
